// Shape raw unified-report API payloads into the structures the report components render.
// Used for the main report, period/budget comparison, monthly trend columns and by-project columns.

const mapAccounts = (accounts) =>
  (accounts || []).map((a) => ({
    code: a.code,
    name: a.name,
    amount: a.amount || a.balance || 0,
  }));

export const normalizePnl = (d = {}) => ({
  summary: {
    total_revenue: d.revenue?.total || 0,
    gross_revenue: d.gross_revenue?.total || d.revenue?.total || 0,
    discount_allowed: d.contra_revenue?.total || 0,
    total_cost_of_sales: d.cost_of_sales?.total || 0,
    gross_profit: d.gross_profit || 0,
    total_operating_expenses: d.operating_expenses?.total || 0,
    total_other_expenses: 0,
    net_profit: d.net_profit || 0,
    profit_margin: d.net_margin_pct || 0,
  },
  gross_revenue: mapAccounts(d.gross_revenue?.accounts || d.revenue?.accounts),
  discount_allowed: mapAccounts(d.contra_revenue?.accounts || []),
  revenue: mapAccounts(d.revenue?.accounts),
  cost_of_sales: mapAccounts(d.cost_of_sales?.accounts),
  operating_expenses: mapAccounts(d.operating_expenses?.accounts),
  other_expenses: [],
  other_comprehensive_income: d.other_comprehensive_income,
  total_comprehensive_income: d.total_comprehensive_income,
  period: d.period,
  currency: d.display_currency || d.currency,
  original_currency: d.original_currency || d.currency,
  fx_rate: d.fx_rate,
  budget: d.budget || null,
  source: 'journals_unified',
});

export const normalizeBalanceSheet = (d = {}) => {
  const mapItems = (items) =>
    (items || []).map((a) => ({
      code: a.code,
      name: a.name,
      balance: a.balance || 0,
    }));
  const sum = (items) => items.reduce((s, a) => s + (a.balance || 0), 0);
  const curAssets = mapItems(d.assets?.current);
  const ncAssets = mapItems(d.assets?.non_current);
  const curLiab = mapItems(d.liabilities?.current);
  const ncLiab = mapItems(d.liabilities?.non_current);
  const eqItems = mapItems(d.equity);
  return {
    assets: {
      total: d.total_assets || 0,
      current: { items: curAssets, total: sum(curAssets) },
      non_current: { items: ncAssets, total: sum(ncAssets) },
    },
    liabilities: {
      total: d.total_liabilities || 0,
      current: { items: curLiab, total: sum(curLiab) },
      non_current: { items: ncLiab, total: sum(ncLiab) },
    },
    equity: {
      total: d.total_equity || 0,
      items: eqItems,
      retained_earnings_current_year: d.retained_earnings_current_year || 0,
      summary: d.equity_summary || {
        share_capital: 0,
        accumulated_retained_earnings: 0,
        current_year_earnings: d.retained_earnings_current_year || 0,
        total_retained_earnings: d.retained_earnings_current_year || 0,
        other_equity: 0,
      },
    },
    equity_summary: d.equity_summary,
    is_balanced: d.is_balanced,
    as_of_date: d.as_of_date,
    currency: d.display_currency || d.currency,
    original_currency: d.original_currency || d.currency,
    fx_rate: d.fx_rate,
    budget: d.budget || null,
    source: 'journals_unified',
  };
};

// Cash Flow (indirect or direct): builds the line items the CashFlowReport renders, for the main
// report, comparison periods and monthly trend columns alike.
export const normalizeCashFlow = (cfData = {}, method = 'indirect') => {
  const isIndirect = (cfData.method || method) === 'indirect';

  // Calculate values - backend returns 'net' field directly
  const operatingNet = cfData.operating_activities?.net ?? 0;
  const investingNet = cfData.investing_activities?.net ?? 0;
  const financingNet = cfData.financing_activities?.net ?? 0;
  const netChange = cfData.summary?.net_change_in_cash ?? operatingNet + investingNet + financingNet;
  const beginningCash = cfData.summary?.beginning_cash ?? cfData.reconciliation?.beginning_cash ?? 0;
  const endingCash = cfData.summary?.ending_cash ?? cfData.reconciliation?.ending_cash ?? 0;

  // Build operating items for the Indirect method.
  const operatingItems = [];
  const ncAdj = cfData.operating_activities?.non_cash_adjustments || {};
  const wcChanges = cfData.operating_activities?.working_capital_changes || {};
  const netProfit = cfData.operating_activities?.net_profit ?? cfData.operating_activities?.net_income ?? 0;
  if (isIndirect) {
    operatingItems.push({
      name: 'Net Profit (from P&L)',
      amount: netProfit,
      code: 'NI',
      bold: true,
    });
    // Non-cash add-backs (positive entries only - zeros hidden)
    const NC_LABELS = {
      depreciation: 'Depreciation',
      amortization: 'Amortization',
      bad_debt: 'Bad Debt Expense',
      fx_loss_unrealized: 'Unrealized FX Loss',
      loss_on_disposal: 'Loss on Disposal of Fixed Assets',
      share_based_compensation: 'Share-Based Compensation',
      fx_gain_unrealized_deducted: 'Unrealized FX Gain (deducted)',
      gain_on_disposal_deducted: 'Gain on Disposal (deducted)',
    };
    Object.entries(NC_LABELS).forEach(([key, label]) => {
      const v = Number(ncAdj[key] || 0);
      if (v !== 0)
        operatingItems.push({
          name: label,
          amount: v,
          code: `NC-${key}`,
          group: 'Non-cash adjustments',
        });
    });
    // Working capital deltas
    const WC_LABELS = {
      change_in_ar: '(Increase) Decrease in Accounts Receivable',
      change_in_inventory: '(Increase) Decrease in Inventory',
      change_in_prepayments: '(Increase) Decrease in Prepayments',
      change_in_tax_receivable: '(Increase) Decrease in Tax Receivable',
      change_in_ap: 'Increase (Decrease) in Accounts Payable',
      change_in_accruals: 'Increase (Decrease) in Accruals',
      change_in_tax_payable: 'Increase (Decrease) in Taxes Payable',
      change_in_deferred_revenue: 'Increase (Decrease) in Deferred Revenue',
      change_in_other_operating_items: 'Change in Other Operating Assets & Liabilities',
    };
    Object.entries(WC_LABELS).forEach(([key, label]) => {
      const v = Number(wcChanges[key] || 0);
      if (v !== 0)
        operatingItems.push({
          name: label,
          amount: v,
          code: `WC-${key}`,
          group: 'Working capital changes',
        });
      if (key === 'change_in_other_operating_items' && v !== 0) {
        (cfData.operating_activities?.other_operating_items_by_gl || []).slice(0, 8).forEach((g) => {
          operatingItems.push({
            name: `· ${g.name || g.code}`,
            amount: Number(g.amount || 0),
            code: `WC-other-${g.code}`,
            group: 'Working capital changes',
            muted: true,
          });
        });
      }
    });
  }

  // Direct method: one row per IAS 7 disclosure line (signed group totals) so exports/prints
  // carry the same lines the screen shows.
  const DIRECT_LABELS = {
    operating: [
      ['receipts_from_customers', 'Cash Receipts from Customers'],
      ['payments_to_suppliers', 'Cash Paid to Suppliers & Vendors'],
      ['payments_to_employees', 'Cash Paid to Employees'],
      ['interest_received', 'Interest Received'],
      ['interest_paid', 'Interest Paid'],
      ['dividends_received', 'Dividends Received'],
      ['taxes_paid', 'Taxes Paid (VAT, WHT, PAYE remittances)'],
      ['income_taxes_paid', 'Income Taxes Paid'],
      ['bank_charges_paid', 'Bank Charges & Fees'],
      ['other_operating', 'Other Operating Activities'],
    ],
    investing: [
      ['proceeds_from_asset_sales', 'Proceeds from Sale of Assets'],
      ['purchases_of_assets', 'Purchase of Fixed Assets'],
      ['investment_proceeds', 'Proceeds from Investments'],
      ['investment_purchases', 'Purchase of Investments'],
    ],
    financing: [
      ['proceeds_from_borrowings', 'Proceeds from Borrowings'],
      ['repayment_of_borrowings', 'Repayment of Borrowings'],
      ['capital_contributions', 'Capital Contributions'],
      ['dividends_paid', 'Dividends Paid'],
      ['owner_drawings', 'Owner Drawings'],
    ],
  };
  const directItems = (sectionKey) =>
    DIRECT_LABELS[sectionKey]
      .map(([key, label]) => ({
        name: label,
        amount: Number(cfData[`${sectionKey}_activities`]?.grouped?.[key]?.total || 0),
        code: `DIR-${key}`,
      }))
      .filter((it) => it.amount !== 0);
  if (!isIndirect) operatingItems.push(...directItems('operating'));

  // Build investing items - pulled from grouped totals (Indirect) or
  // signed disclosure-line totals (Direct).
  const investingItems = isIndirect ? [] : directItems('investing');
  const investingGrouped = isIndirect
    ? {
        purchases_of_fixed_assets: {
          label: 'Purchase of Fixed Assets',
          neg: true,
        },
        proceeds_from_sale_of_fixed_assets: {
          label: 'Proceeds from Sale of Fixed Assets',
          neg: false,
        },
        purchases_of_intangibles: {
          label: 'Purchase of Intangible Assets',
          neg: true,
        },
        proceeds_from_sale_of_intangibles: {
          label: 'Proceeds from Sale of Intangibles',
          neg: false,
        },
        purchases_of_investments: {
          label: 'Purchase of Investments',
          neg: true,
        },
        proceeds_from_investments: {
          label: 'Proceeds from Investments',
          neg: false,
        },
        loans_given_disbursed: {
          label: 'Loans Disbursed (Receivable)',
          neg: true,
        },
        loans_given_repayments_received: {
          label: 'Loan Repayments Received',
          neg: false,
        },
      }
    : null;
  if (isIndirect && investingGrouped) {
    Object.entries(investingGrouped).forEach(([key, cfg]) => {
      const v = Number(cfData.investing_activities?.[key] || 0);
      if (v !== 0)
        investingItems.push({
          name: cfg.label,
          amount: cfg.neg ? -v : v,
          code: `INV-${key}`,
        });
    });
  }

  // Financing items
  const financingItems = isIndirect ? [] : directItems('financing');
  if (isIndirect) {
    const FIN_MAP = {
      proceeds_from_borrowings: {
        label: 'Proceeds from Borrowings',
        neg: false,
      },
      repayment_of_borrowings: { label: 'Repayment of Borrowings', neg: true },
      capital_contributions: {
        label: 'Capital Contributions / Share Issuance',
        neg: false,
      },
      owner_drawings: { label: 'Owner Drawings', neg: true },
      dividends_paid: { label: 'Dividends Paid', neg: true },
    };
    Object.entries(FIN_MAP).forEach(([key, cfg]) => {
      const v = Number(cfData.financing_activities?.[key] || 0);
      if (v !== 0)
        financingItems.push({
          name: cfg.label,
          amount: cfg.neg ? -v : v,
          code: `FIN-${key}`,
        });
    });
  }

  // Transform to expected format for CashFlowReport component
  return {
    source: 'journals_unified',
    method: cfData.method || method,
    period: cfData.period,
    currency: cfData.display_currency || cfData.currency,
    original_currency: cfData.original_currency || cfData.currency,
    fx_rate: cfData.fx_rate,
    // Phase 4 - budget overlay payload
    budget: cfData.budget || null,
    operating: {
      net: operatingNet,
      net_income: netProfit,
      items: operatingItems,
      non_cash_adjustments_total: Number(ncAdj.total || 0),
      working_capital_total: Number(wcChanges.total || 0),
      transactions: cfData.operating_activities?.transactions || [],
      grouped: cfData.operating_activities?.grouped || {},
    },
    investing: {
      net: investingNet,
      items: investingItems,
      transactions: cfData.investing_activities?.transactions || [],
      grouped: cfData.investing_activities?.grouped || {},
    },
    financing: {
      net: financingNet,
      items: financingItems,
      transactions: cfData.financing_activities?.transactions || [],
      grouped: cfData.financing_activities?.grouped || {},
    },
    net_change: netChange,
    beginning_cash: beginningCash,
    ending_cash: endingCash,
    opening_cash: beginningCash,
    closing_cash: endingCash,
    reconciliation: cfData.reconciliation || null,
    summary: {
      opening_cash: beginningCash,
      beginning_cash: beginningCash,
      net_change: netChange,
      closing_cash: endingCash,
      ending_cash: endingCash,
    },
  };
};

// Section/row layout shared by the on-screen multi-column statement and its exports.
export const statementLayout = (reportType, { cogsLabel = 'Cost of Sales', netLabel = 'Net Profit' } = {}) => {
  if (reportType === 'pnl') {
    return {
      sums: true,
      sections: [
        {
          key: 'revenue',
          title: 'Revenue',
          color: 'text-[#108981]',
          items: (d) => d?.revenue || [],
          total: (d) => d?.summary?.total_revenue || 0,
          totalLabel: 'Total Revenue',
        },
        {
          key: 'cogs',
          title: cogsLabel,
          color: 'text-orange-600',
          items: (d) => d?.cost_of_sales || [],
          total: (d) => d?.summary?.total_cost_of_sales || 0,
          totalLabel: `Total ${cogsLabel}`,
        },
        {
          key: 'gross_profit',
          highlight: 'blue',
          title: 'Gross Profit',
          total: (d) => d?.summary?.gross_profit || 0,
        },
        {
          key: 'operating',
          title: 'Operating Expenses',
          color: 'text-[#283F8B]',
          items: (d) => d?.operating_expenses || [],
          total: (d) => d?.summary?.total_operating_expenses || 0,
          totalLabel: 'Total Operating Expenses',
        },
        {
          key: 'other',
          title: 'Other Expenses',
          color: 'text-gray-600',
          items: (d) => d?.other_expenses || [],
          total: (d) => d?.summary?.total_other_expenses || 0,
          totalLabel: 'Total Other Expenses',
          optional: true,
        },
        {
          key: 'net_profit',
          highlight: 'green',
          title: netLabel,
          total: (d) => d?.summary?.net_profit || 0,
        },
      ],
    };
  }
  if (reportType === 'balance_sheet') {
    return {
      sums: false,
      sections: [
        {
          key: 'current_assets',
          title: 'Current Assets',
          color: 'text-[#108981]',
          items: (d) => d?.assets?.current?.items || [],
          total: (d) => d?.assets?.current?.total || 0,
          totalLabel: 'Total Current Assets',
        },
        {
          key: 'non_current_assets',
          title: 'Non-Current Assets',
          color: 'text-[#108981]',
          items: (d) => d?.assets?.non_current?.items || [],
          total: (d) => d?.assets?.non_current?.total || 0,
          totalLabel: 'Total Non-Current Assets',
        },
        {
          key: 'total_assets',
          highlight: 'blue',
          title: 'Total Assets',
          total: (d) => d?.assets?.total || 0,
        },
        {
          key: 'current_liabilities',
          title: 'Current Liabilities',
          color: 'text-orange-600',
          items: (d) => d?.liabilities?.current?.items || [],
          total: (d) => d?.liabilities?.current?.total || 0,
          totalLabel: 'Total Current Liabilities',
        },
        {
          key: 'non_current_liabilities',
          title: 'Non-Current Liabilities',
          color: 'text-orange-600',
          items: (d) => d?.liabilities?.non_current?.items || [],
          total: (d) => d?.liabilities?.non_current?.total || 0,
          totalLabel: 'Total Non-Current Liabilities',
        },
        {
          key: 'equity',
          title: 'Equity',
          color: 'text-[#283F8B]',
          items: (d) => d?.equity?.items || [],
          total: (d) => d?.equity?.total || 0,
          totalLabel: 'Total Equity',
        },
        {
          key: 'total_le',
          highlight: 'green',
          title: 'Total Liabilities & Equity',
          total: (d) => (d?.liabilities?.total || 0) + (d?.equity?.total || 0),
        },
      ],
    };
  }
  return {
    sums: true,
    sections: [
      {
        key: 'opening',
        highlight: 'slate',
        title: 'Opening Cash',
        total: (d) => d?.summary?.opening_cash || 0,
        noSum: true,
      },
      {
        key: 'operating',
        highlight: 'blue',
        title: 'Cash from Operating Activities',
        total: (d) => d?.operating?.net || 0,
      },
      {
        key: 'investing',
        highlight: 'blue',
        title: 'Cash from Investing Activities',
        total: (d) => d?.investing?.net || 0,
      },
      {
        key: 'financing',
        highlight: 'blue',
        title: 'Cash from Financing Activities',
        total: (d) => d?.financing?.net || 0,
      },
      {
        key: 'net_change',
        highlight: 'green',
        title: 'Net Change in Cash',
        total: (d) => d?.net_change || 0,
      },
      {
        key: 'closing',
        highlight: 'slate',
        title: 'Closing Cash',
        total: (d) => d?.summary?.closing_cash || 0,
        noSum: true,
      },
    ],
  };
};

// Merge item lists from every column into one row list (by GL code, else name).
export const unionItems = (columns, itemsOf) => {
  const rows = new Map();
  columns.forEach((c) => {
    itemsOf(c.data).forEach((it) => {
      const k = it.code || it.name;
      if (!rows.has(k)) rows.set(k, { code: it.code, name: it.name, values: {} });
      rows.get(k).values[c.key] = (rows.get(k).values[c.key] || 0) + (it.balance ?? it.amount ?? 0);
    });
  });
  return Array.from(rows.values()).sort((a, b) => String(a.code || a.name).localeCompare(String(b.code || b.name)));
};

// Flat rows for CSV / Excel: one column per statement column (+ Total when the layout sums).
export const multiColumnExportRows = (reportType, columns, opts = {}) => {
  const layout = statementLayout(reportType, opts);
  const out = [];
  const withTotal = (row, valueOf, noSum = false) => {
    columns.forEach((c) => {
      row[c.label] = Number(valueOf(c)) || 0;
    });
    if (layout.sums && !noSum) row.Total = columns.reduce((s, c) => s + (Number(valueOf(c)) || 0), 0);
    return row;
  };
  layout.sections.forEach((sec) => {
    if (sec.highlight) {
      out.push(withTotal({ Section: sec.title, Code: '', Account: sec.title }, (c) => sec.total(c.data), sec.noSum));
      return;
    }
    const items = unionItems(columns, sec.items);
    if (sec.optional && !items.length) return;
    items.forEach((it) =>
      out.push(withTotal({ Section: sec.title, Code: it.code || '', Account: it.name }, (c) => it.values[c.key] || 0)),
    );
    out.push(withTotal({ Section: sec.title, Code: '', Account: sec.totalLabel }, (c) => sec.total(c.data)));
  });
  return out;
};
